import { MinusIcon, NavAddCircle } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  AutocompleteInput,
  Button,
  InputMessage,
  Paragraph,
  TextInput,
  fontWeights,
  paragraphSizes,
} from '@va/ui/design-system';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { TextFilter, TextFilterValue } from './TextFilter';

export const MultiSelectFields = ({
  filter,
  placeholder,
  error,
  setAllValues,
  allValues,
  debounced = false,
}: {
  filter: TextFilter;
  placeholder?: string;
  error?: string;
  setAllValues: Dispatch<SetStateAction<TextFilterValue[]>>;
  allValues: TextFilterValue[];
  debounced?: boolean;
}) => {
  const { inputProps, allowMultiSelect, autocompleteOptionsFetcher } = filter;
  const translate = useTranslate();

  const shouldDisplaySuggestions = !!autocompleteOptionsFetcher;

  const handleSubValuesChange = useCallback(
    (indexVal: number, value: TextFilterValue) => {
      setAllValues((prev) => {
        const updatedData = prev.map((item, index) => {
          if (index === indexVal) {
            return value;
          }
          return item;
        });
        return updatedData;
      });
    },
    [setAllValues],
  );

  const handleRemove = useCallback(
    (indexVal: number) => {
      setAllValues((prev) => {
        return prev.filter((item, index) => index !== indexVal);
      });
    },
    [setAllValues],
  );

  const handleClearField = useCallback(
    (indexVal: number) => {
      setAllValues((prev) =>
        prev.map((item, index) => {
          if (index === indexVal) {
            return '';
          }
          return item;
        }),
      );
    },
    [setAllValues],
  );

  return (
    <div className='flex flex-col overflow-hidden'>
      <div className='flex flex-col max-h-72 gap-3 overflow-auto scrollbar scrollbar-thumb'>
        {allValues.map((value, index) => {
          if (index === 0) {
            if (shouldDisplaySuggestions) {
              return (
                <AutocompleteInput
                  key={index}
                  dataTestId='text-filter-input'
                  debouncedInput={true}
                  value={value}
                  clearField={() => {
                    handleSubValuesChange(index, '');
                  }}
                  onChange={(e) => {
                    handleSubValuesChange(index, e.target.value);
                  }}
                  placeholder={placeholder}
                  label={inputProps?.label}
                  error={error && !value && translate(error)}
                  suggestions={autocompleteOptionsFetcher ?? []}
                  onSuggestionClick={(val) => {
                    handleSubValuesChange(index, val);
                  }}
                />
              );
            }

            return (
              <TextInput
                key={index}
                dataTestId='text-filter-input'
                debouncedInput={debounced}
                value={value}
                clearField={() => {
                  handleSubValuesChange(index, '');
                }}
                onChange={(e) => {
                  handleSubValuesChange(index, e.target.value);
                }}
                placeholder={placeholder}
                label={inputProps?.label}
                error={error && !value && translate(error)}
              />
            );
          }
          if (!allowMultiSelect) return null;
          return (
            <OrOption
              key={index}
              placeholder={placeholder}
              value={value}
              onChange={(val) => handleSubValuesChange(index, val)}
              error={error && !value && translate(error)}
              onRemove={() => handleRemove(index)}
              clearField={() => handleClearField(index)}
              debounced={debounced}
              filter={filter}
              shouldDisplaySuggestions={shouldDisplaySuggestions}
            />
          );
        })}
      </div>
      {filter.allowMultiSelect && (
        <Button
          icon={(_, color) => <NavAddCircle className='w-15px h-15px' color={color} />}
          text={filter.multiSelectButtonLabel ?? translate('filters.labels.multiSelect.addValue')}
          color='tertiary'
          className='w-full justify-center shrink-0 mt-1 px-18px py-9px'
          onClick={() => setAllValues((prev) => [...prev, ''])}
          size='small'
          textClasses='text-xs leading-18'
        />
      )}
    </div>
  );
};

export const OrOption = ({
  placeholder,
  onChange,
  value,
  error,
  onRemove,
  clearField,
  debounced = false,
  filter,
  shouldDisplaySuggestions,
}: {
  placeholder?: string;
  onChange: (value: string) => void;
  value?: string | number;
  error?: string;
  onRemove: () => void;
  clearField: () => void;
  debounced?: boolean;
  filter: TextFilter;
  shouldDisplaySuggestions: boolean;
}) => {
  const translate = useTranslate();
  return (
    <div className='flex flex-col'>
      <div className='flex gap-[0.125rem]'>
        <Paragraph
          weight={fontWeights.medium}
          size={paragraphSizes.tiny}
          className='h-12 rounded-l-xl flex items-center justify-center px-3 bg-white-snow'
        >
          {translate('all.filters.combinationalOperators.or')}
        </Paragraph>
        {shouldDisplaySuggestions ? (
          <AutocompleteInput
            dataTestId='text-filter-input'
            debouncedInput={true}
            value={value}
            clearField={clearField}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className='flex-1'
            heightClass='h-12'
            inputWrapperClassName='!rounded-none'
            suggestions={filter.autocompleteOptionsFetcher ?? []}
            onSuggestionClick={onChange}
          />
        ) : (
          <TextInput
            dataTestId='text-filter-input'
            debouncedInput={debounced}
            value={value}
            clearField={clearField}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            wrapperClassName='w-full'
            heightClass='h-12'
            inputWrapperClassName='!rounded-none'
          />
        )}

        <Button
          icon={(_, color) => <MinusIcon color={color} />}
          color='tertiary'
          className='rounded-l-none'
          onClick={onRemove}
        />
      </div>
      {error && <InputMessage error={error} />}
    </div>
  );
};
